import React, { useCallback, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router';
import { AppButton, Box, FlexControlledForm, Preloader, Text } from '@common-fe/common-fe';

import { WarnModal } from '@/components/elements';
import { ICardReplaceDtoReasonEnum } from '@/dtos';
import { usePreferredMailingAdressStore } from '@/modules/employee/Employee/PersonalInformation/PreferredMailingAddress/stores';
import useFraudConfirmationQuery from '@/modules/transaction/CardDetails/queries/useFraudConfirmation.query';
import { ReplacementInitiationType } from '@/modules/transaction/Cards/Cards.types';
import { useGetCardsQuery } from '@/modules/transaction/Cards/queries/useGetCards.query';
import theme from '@/styles/theme';

import useCardReplaceModalStore from '../../store/useCardReplaceModal.store';

import useCardAddressModalFields from './hooks/useCardAddressModalFields';
import useCardReplacementQuery from './hooks/useCardReplacement.query';
import useCardReplacementReasonFields, { FIELDS, FormValues } from './hooks/useCardReplacementReasonFields';

const EMPTY_VALUE = '-';

interface Props {
  onCancel?: (value: boolean) => void;
  setDoneMode?: () => void;
  loading?: boolean;
}

const CardReplaceFormModal: React.FC<Props> = ({ onCancel, setDoneMode, loading }) => {
  const [cardSetupReason, setCardSetupReason] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const {
    params: { id: employeeId },
  } = useRouteMatch<{ id: string }>();
  const { refetch } = useGetCardsQuery(employeeId);
  const [isSubmit, setSubmit] = useState(false);
  const { onSave, isLoading } = useCardReplacementQuery();
  const { confirmFraud } = useFraudConfirmationQuery();
  const defaultFields = usePreferredMailingAdressStore((state) => state.sourceState);

  const currentAddressParams = useMemo(
    () => ({
      STREET_ADDRESS_1: defaultFields?.firstAddressLine || EMPTY_VALUE,
      STREET_ADDRESS_2: defaultFields?.secondAddressLine || EMPTY_VALUE,
      CITY: defaultFields?.city || EMPTY_VALUE,
      STATE: defaultFields?.state || EMPTY_VALUE,
      ZIP_CODE: defaultFields?.zipCode || EMPTY_VALUE,
    }),
    [defaultFields]
  );

  const addressFields = useCardAddressModalFields({
    defaultFields: currentAddressParams,
  });

  const {
    state: { cardID },
    handleReset,
  } = useCardReplaceModalStore();

  const [formValues, setFormValues] = useState<FormValues>();
  const replacementFields = useCardReplacementReasonFields({ formValues });

  const showError = useMemo(() => isSubmit && !cardSetupReason, [cardSetupReason, isSubmit]);

  const handleChangeValues = useCallback((values: FormValues) => {
    const isFraudEnabled =
      values?.[FIELDS.REPLACEMENT_REASON] === ICardReplaceDtoReasonEnum.LOST ||
      values?.[FIELDS.REPLACEMENT_REASON] === ICardReplaceDtoReasonEnum.STOLEN;
    setFormValues((prev) => ({
      ...prev,
      ...values,
      ...(isFraudEnabled ? {} : { [FIELDS.CONFIRM_FRAUD]: false }),
    }));
    if (values?.reason) {
      setCardSetupReason(true);
    } else {
      setCardSetupReason(false);
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmit(true);
    if (cardSetupReason && setDoneMode) {
      try {
        await onSave({
          cardID,
          data: {
            reason: formValues?.reason,
            replacement_initiation_type: ReplacementInitiationType.ADMIN_PORTAL,
          },
          replacementInitiationType: ReplacementInitiationType.ADMIN_PORTAL,
        });
        if (formValues?.isConfirmFraud) {
          confirmFraud(cardID);
        }
        refetch();
        setDoneMode();
      } catch {
        setErrorModal(true);
      }
    }
  }, [cardSetupReason, setDoneMode, onSave, cardID, formValues, refetch, confirmFraud]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel(false);
      handleReset();
    }
  }, [handleReset, onCancel]);

  return (
    <>
      <Box>
        <Box direction="column">
          <Box round="container1Round" pad="spacing24" background="module">
            <Box data-testid="ReplacementReasonForm" round="container1Round" background="canvas">
              <FlexControlledForm
                // formTitle="Why you need a new card?"
                fields={replacementFields}
                editMode
                onChangeValues={handleChangeValues}
                showError={showError}
              />
            </Box>
            {cardSetupReason && (
              <Box
                data-testid="ReplacementAddressForm"
                round="container1Round"
                background="canvas"
                margin={{ top: 'spacing24' }}
              >
                <FlexControlledForm
                  formTitle="Your Card Will Be Sent To"
                  subTitle={
                    <Box margin={{ top: 'spacing4', bottom: '20px' }}>
                      <Text size="12px" weight={500} color="textSecondary">
                        If this address is incorrect, please update with your employer.
                      </Text>
                    </Box>
                  }
                  fields={addressFields}
                  editMode
                  onChangeValues={() => null}
                  isModalType
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box
          direction="row"
          fill="horizontal"
          margin={{ top: showError ? 'spacing12' : 'spacing24' }}
          align="end"
          justify="end"
          width="medium"
        >
          <Box width="control" margin={{ right: 'spacing8' }}>
            <AppButton
              type="button"
              buttonType="secondary"
              onClick={handleCancel}
              width="100%"
              containerStyle={{ marginRight: theme.spacings.spacing8 }}
            >
              Cancel
            </AppButton>
          </Box>
          <Box width="control">
            <AppButton disabled={loading || isLoading} width="100%" onClick={handleSubmit}>
              {loading || isLoading ? <Preloader color="white" /> : 'Submit'}
            </AppButton>
          </Box>
        </Box>
      </Box>
      <WarnModal
        testId="replace-error-modal"
        visible={errorModal}
        onSetVisible={setErrorModal}
        header="The server encountered an error processing the request"
        helptext={`It doesn't appear to have affected your data, but we cannot process your request.
        Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`}
        buttonText="Close"
        onSubmit={handleSubmit}
        submitButtonText="Try again"
      />
    </>
  );
};

export default CardReplaceFormModal;
